/**
 * Builds the list of years to offer in the mobility budget year selector.
 *
 * Always includes a fixed window around the current year (two years back
 * through one year ahead), plus every year spanned by the employee's current
 * lease, if provided, so a lease that started or ends outside that window is
 * still selectable.
 *
 * @param leaseStartDate - ISO `yyyy-MM-dd` lease start date, or `null`/`undefined` if no lease.
 * @param leaseEndDate - ISO `yyyy-MM-dd` lease end date, or `null`/`undefined` if open-ended/no lease.
 * @param currentYear - The reference year; defaults to the current calendar year.
 * @returns Sorted, deduplicated years to show as options.
 */
export function getMobilityYearOptions(
  leaseStartDate?: string | null,
  leaseEndDate?: string | null,
  currentYear = new Date().getFullYear(),
) {
  // Confirmed range: two years of history plus one year ahead for planning.
  const years = new Set<number>([
    currentYear - 2,
    currentYear - 1,
    currentYear,
    currentYear + 1,
  ]);

  const leaseStartYear = parseIsoYear(leaseStartDate);
  const leaseEndYear = parseIsoYear(leaseEndDate);
  if (leaseStartYear && leaseEndYear && leaseEndYear >= leaseStartYear) {
    for (let year = leaseStartYear; year <= leaseEndYear; year += 1) years.add(year);
  }

  return [...years].sort((left, right) => left - right);
}

/**
 * Computes the portion of a given calendar year during which the lease was
 * (or will be) active, clamped to that year's bounds.
 *
 * @param year - The calendar year to compute the period for.
 * @param leaseStartDate - ISO `yyyy-MM-dd` lease start date, or `null`/`undefined` if unbounded.
 * @param leaseEndDate - ISO `yyyy-MM-dd` lease end date, or `null`/`undefined` if open-ended.
 * @returns The active period's `{ activeFrom, activeUntil }` ISO dates, or `null` if the
 *   lease does not overlap this year at all.
 */
export function getBudgetPeriodForYear(
  year: number,
  leaseStartDate?: string | null,
  leaseEndDate?: string | null,
) {
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const activeFrom = leaseStartDate && leaseStartDate > yearStart ? leaseStartDate : yearStart;
  const activeUntil = leaseEndDate && leaseEndDate < yearEnd ? leaseEndDate : yearEnd;

  return activeFrom <= activeUntil ? { activeFrom, activeUntil } : null;
}

/**
 * Extracts the calendar year from an ISO `yyyy-MM-dd` date string.
 *
 * @param value - The ISO date string, or `null`/`undefined`.
 * @returns The year as a number, or `null` if `value` is missing or not in ISO date format.
 */
function parseIsoYear(value?: string | null) {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const year = Number(value.slice(0, 4));
  return Number.isInteger(year) ? year : null;
}
